import { useArePrecedingMilestonesCompleted } from '@/hooks/useArePrecedingMilestonesCompleted';
import { Alert, Box, Button, Card, toast } from '@palmetto/palmetto-components';
import { Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  ActivationDocumentTypes,
  ActivationPackagePhotoCategory,
  MilestoneReviewStatus,
  MilestoneType,
  QueueStatus,
  QueueType,
  TaskType,
  validateMonitoringIdFormat,
} from 'types';

import { convertToISODateTimeString } from '../../helpers/dates';
import { useGetAccountQuery } from '../../services/accounts';
import { useSubmitActivationPacketMutation } from '../../services/activationPacket';
import { isErrorWithData } from '../../services/helpers';
import { useGetQueueByAccountIdAndTypeQuery } from '../../services/queues';
import { useGetTasksQuery } from '../../services/tasks';
import { ActivationPackageActions } from '../../types/ActivationPackage';
import { Task, taskToMilestoneMap } from '../../types/Tasks';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { Documents } from '../Documents';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';

import { ActivationPhotos } from './ActivationPhotos';
import { MonitoringSiteIdSection } from './MonitoringSiteIdSection';
import { PTOConfirmationSection } from './PTOConfirmationSection';
import { taskTypeToActivationPhotoSection } from './taskTypeToDocumentType';

const activationDocumentsDescription = (
  <Box color="body-secondary" fontSize="sm" childGap="2xs">
    <Box as="p">Upload all required incentives documents:</Box>
    <Box childGap="2xs" as="ul">
      <li>Extended Warranty</li>
      <li>Incentives</li>
    </Box>
  </Box>
);

const getMostRecentObj = (array: any[]) => {
  return array?.reduce((prev: any, current: any) => {
    return prev.date > current.date ? prev : current;
  });
};

export const LegacyActivationPackage = () => {
  const { id, action } = useParams<{ id: any; action: ActivationPackageActions }>();
  const { data: items = [], isLoading } = useGetTasksQuery({ id });
  const { data: account } = useGetAccountQuery(id);
  const { data: queue } = useGetQueueByAccountIdAndTypeQuery({
    accountId: id,
    type: QueueType.installPackage,
  });
  const [submitActivationPacket] = useSubmitActivationPacketMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const allowsAction = useIsOrgReadOnly();

  const installNote = useMemo(() => {
    if (!account) {
      return;
    }

    if (queue && queue.status === QueueStatus.conditionallyApproved) {
      return getMostRecentObj(queue.approvalNotes)?.note;
    }

    return account?.notes?.milestones?.install;
  }, [account, queue]);

  const canSubmitActivationPackage = useArePrecedingMilestonesCompleted(
    account?.milestones,
    MilestoneType.activation,
  );

  const initialValues = useMemo(
    () => ({
      monitoringSiteId: account?.monitoringSiteId || '',
      ptoGrantedDate: account?.ptoGrantedDate || '',
    }),
    [account],
  );
  const incompleteAdminTasks = useMemo(() => {
    return items.filter(
      (item) =>
        item.assigneeType === 'internal' &&
        item.status === 'ready' &&
        taskToMilestoneMap[item.type] === MilestoneType.systemActivation,
    );
  }, [items]);

  const activationPhotoPartnerTasks = useMemo(() => {
    if (isLoading) return [];

    if (incompleteAdminTasks?.length === 0 && action === ActivationPackageActions.edit) {
      const partnerTasks = items.filter((item) => {
        return (
          item.assigneeType === 'partner' &&
          item.status === 'ready' &&
          taskToMilestoneMap[item.type] === MilestoneType.systemActivation
        );
      });
      if (partnerTasks?.length) {
        const filesToUpload: ActivationPackagePhotoCategory[] = [];
        partnerTasks.forEach((task: any) => {
          if (taskTypeToActivationPhotoSection[task.type]) {
            filesToUpload.push(taskTypeToActivationPhotoSection[task.type]);
          }
        });
        return filesToUpload;
      }
    }

    return [];
  }, [items, action, isLoading, incompleteAdminTasks]);

  const rejectedTasks = useMemo(() => {
    if (!items) {
      return;
    }
    const internalActivationTasks = items.filter(
      (item: any) =>
        item.assigneeType === 'internal' &&
        taskToMilestoneMap[item.type as TaskType] === MilestoneType.systemActivation,
    );
    const groupedTasks = internalActivationTasks.reduce(
      (acc: any, task: any) => {
        acc[task.type] = acc[task.type] || [];
        acc[task.type].push(task);
        return acc;
      },
      {} as Record<TaskType, Task[]>,
    );
    return Object.keys(groupedTasks).reduce((acc, taskType) => {
      const tasks = groupedTasks[taskType as TaskType];
      if (!tasks.every((task: any) => task.rejectionReasons?.length && task.status === 'rejected')) {
        return acc;
      }
      acc.push(taskType);
      return acc;
    }, [] as string[]);
  }, [items]);

  useEffect(() => {
    if (!action || !ActivationPackageActions[action]) {
      navigate(`/accounts/${id}`, { replace: true });
    }
  }, [id, navigate, action]);

  const isActivationPendingReview = action === ActivationPackageActions.edit && !!incompleteAdminTasks.length;

  const handleSendRejectedPackageEmail = async () => {
    const ptoGrantedDate = account.ptoGrantedDate;
    const monitoringSiteId = account.monitoringSiteId;
    try {
      setIsSubmitting(true);
      await submitActivationPacket({
        accountId: id,
        data: {
          ptoGrantedDate: convertToISODateTimeString(ptoGrantedDate) || '',
          monitoringSiteId,
          action,
          reviewStatus: MilestoneReviewStatus.rejected,
        },
      }).unwrap();

      toast.success('Activation Package Rejection email sent');
      navigate(`/accounts/${id}`);
    } catch (e) {
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(`Error sending email: ${errorMessage}`);
      } else {
        console.error(e);
        toast.error('Error sending email');
      }
    }
    setIsSubmitting(false);
  };

  const handleSubmit = async (values: any) => {
    // when adding submitActivationPacket for the new 'complete review' button, the account will be updated again
    // and values for ptoGrantedDate and monitoringSiteId will be undefined
    // to fix this, we will use the account values if the form values are undefined
    let ptoGrantedDate = values.ptoGrantedDate;
    if (!ptoGrantedDate) {
      ptoGrantedDate = account.ptoGrantedDate;
    }
    let monitoringSiteId = values.monitoringSiteId;
    if (!monitoringSiteId) {
      monitoringSiteId = account.monitoringSiteId;
    }
    try {
      setIsSubmitting(true);
      await submitActivationPacket({
        accountId: id,
        data: { ...values, ptoGrantedDate: new Date(ptoGrantedDate), monitoringSiteId, action },
      }).unwrap();

      if (action !== ActivationPackageActions.review) {
        toast.success('Activation Package submitted successfully');
        navigate(`/accounts/${id}`);
      }
    } catch (e) {
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(`Error submitting package: ${errorMessage}`);
      } else {
        console.error(e);
        toast.error('Error submitting package');
      }
    }
    setIsSubmitting(false);
  };

  const handleValidation = (values: any) => {
    const errors = {} as any;
    if (!values.ptoGrantedDate) {
      errors.ptoGrantedDate = 'PTO Granted Date is required';
    }
    const inverterManufacturer =
      account?.systemDesign?.inverters?.[0]?.manufacturer || account?.systemDesign?.inverterManufacturer;
    const monitoringIdFormat = validateMonitoringIdFormat(values.monitoringSiteId, inverterManufacturer);
    if (!values.monitoringSiteId.trim()) {
      errors.monitoringSiteId = 'A Monitoring Site Id is required';
    } else if (!monitoringIdFormat.valid) {
      errors.monitoringSiteId = `Monitoring Site ID must match the required format: ${monitoringIdFormat.expectedFormat}.`;
    }

    return errors;
  };

  return (
    <>
      <MainContainer>
        {isActivationPendingReview && (
          <Alert
            variant="warning"
            hasIcon
            title="Activation Package Pending Review"
            message="The Activation Package is pending review. The review must be completed before resubmitting."
          />
        )}
        {installNote && <Alert variant="info" hasIcon title="Install Notes" message={installNote} />}
        <Box padding={{ base: '0 lg', tablet: '0' }}>
          <Formik
            initialValues={initialValues}
            validate={handleValidation}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            <EnhancedFormikForm noValidate id="activationMilestoneForm">
              <Box>
                <MonitoringSiteIdSection />
              </Box>
              <Box>
                <PTOConfirmationSection isActivationPendingReview={isActivationPendingReview} />
              </Box>
            </EnhancedFormikForm>
          </Formik>
          <FormSection title="Incentives Documents" description={activationDocumentsDescription}>
            <Documents
              documentTypeFilter={[ActivationDocumentTypes.extendedWarranty, ActivationDocumentTypes.incentives]}
              showDocumentActions={action === ActivationPackageActions.review}
              allowArchive={false}
              title="Incentives"
              showUploadButton={action !== ActivationPackageActions.review && !isActivationPendingReview}
              originContext={QueueType.activationPackage}
            />
          </FormSection>

          <FormSection
            title="Site Improvements"
            description="If applicable, photos of site improvements made such as tree removal or main panel upgrade."
          >
            <Card>
              <ActivationPhotos
                partnerReviewTaskSections={activationPhotoPartnerTasks}
                isActivationPendingReview={isActivationPendingReview}
              />
            </Card>
          </FormSection>
          {rejectedTasks && rejectedTasks.length > 0 && action === ActivationPackageActions.review && (
            <Box
              direction={{
                base: 'column',
                tablet: 'row',
              }}
              alignItems={{
                base: 'center',
                tablet: 'center',
              }}
              justifyContent={{
                tablet: 'flex-end',
              }}
              childGap="sm"
              style={{ flexShrink: 0 }}
              padding="lg 0 0 0"
            >
              <Box alignItems="center" color="danger">
                {`${rejectedTasks.length} ${rejectedTasks.length > 1 ? 'items are' : 'item is'} rejected`}
              </Box>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                onClick={handleSendRejectedPackageEmail}
              >
                Send Package Rejection Email
              </Button>
            </Box>
          )}
          {(action === ActivationPackageActions.create || action === ActivationPackageActions.edit) && (
            <Box
              direction={{
                base: 'column',
                tablet: 'row',
              }}
              alignItems={{
                base: 'stretch',
                tablet: 'flex-end',
              }}
              justifyContent={{
                tablet: 'flex-end',
              }}
              childGap="sm"
              style={{ flexShrink: 0 }}
              padding="lg 0 0 0"
            >
              <Button
                as="a"
                navigate={() => navigate(`/accounts/${id}`)}
                variant="secondary"
                tone="neutral"
                isLoading={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="activationMilestoneForm"
                isDisabled={!!incompleteAdminTasks?.length || !canSubmitActivationPackage || !allowsAction}
                isLoading={isSubmitting}
                variant="primary"
              >
                Submit Activation Package
              </Button>
            </Box>
          )}
        </Box>
      </MainContainer>
    </>
  );
};
